import React, { useContext } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import { useMediaQuery } from 'react-responsive';

// Redux
import { useSelector } from 'react-redux';
import { getCurrentUser } from 'redux/selectors/users';
import { getAiRolePlay } from 'redux/selectors/timeline';

import {
  ReactComponent as AIPersona,
} from 'styles/icons/ai-persona.svg';
import ProfilePicture from 'org_level_profile/components/profile-picture';
import { Button } from 'react-bootstrap';
import {
  doubleSpacing,
  quarterSpacing,
  shortBar,
  standardSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { genAIGradientType4 } from 'styles/global_defaults/colors';
import { screenSmMax, screenXsMax } from 'styles/global_defaults/media-queries';
import AiConversationContext from './context';
import ConversationModalHeader from './header';
import ScenarioDetails from './scenario-details';
import ChatBar from './chat-bar';
import AiChat from './ai-chat';
import LearnerChat from './learner-chat';

const ConversationModalContent = () => {
  const isMobile = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });
  const isTabletOrSmaller = useMediaQuery({
    query: `(max-width: ${screenSmMax}px)`,
  });

  const getResponsivePadding = () => {
    if (isMobile) {
      return standardSpacing;
    }
    if (isTabletOrSmaller) {
      return doubleSpacing;
    }
    return shortBar;
  };

  const styles = css`
  border-top: ${quarterSpacing}px solid transparent;
  border-image-source: ${genAIGradientType4};
  border-image-slice: 1;
  .body-container {
    flex: 1 1 auto;
    min-height: 0;
    padding-left: ${getResponsivePadding()}px;
    padding-right: ${getResponsivePadding()}px;
    overflow-y: auto;
    .scenario-container {
      flex-grow: 1;
      .avatar-container {
        .profile {
          width: ${shortBar}px;
        }
      }
      .scenario-toggle-button {
        margin-top: 25px;
      }
    }
  }
`;

  const {
    aiRolePlayId,
    isConversationStarted,
    setIsConversationStarted,
    showScenarioDetails,
    setShowScenarioDetails,
  } = useContext(AiConversationContext);
  const currentUser = useSelector(getCurrentUser);
  const aiRolePlay = useSelector((state) => getAiRolePlay(state, aiRolePlayId));

  const {
    aiCharacterName,
    aiCharacterRole,
    learnerRole,
  } = aiRolePlay || {};

  const handleToggleScenarioDetails = () => {
    setShowScenarioDetails(!showScenarioDetails);
  };

  const handleStartConversation = () => {
    setShowScenarioDetails(false);
    setIsConversationStarted(true);
  };

  return (
    <div css={styles} className='modal-page position-relative d-flex flex-column'>
      <ConversationModalHeader />
      <div className='body-container border-top d-flex flex-column'>
        <div className='mt-5 scenario-container'>
          <div className='avatar-container d-flex justify-content-center'>
            <div className='profile d-flex flex-column justify-content-center align-items-center mt-2'>
              <AIPersona
                width={tripleSpacing}
                height={tripleSpacing}
                role='img'
                aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.AI_CHARACTER_AVATAR()}
              />
              <p className='mb-0 mt-2 text-regular semi-bold'>{aiCharacterName}</p>
              <p className='mb-0 text-small text-gray-2'>{aiCharacterRole}</p>
            </div>
            <div className='profile d-flex flex-column justify-content-center align-items-center mt-2'>
              <ProfilePicture
                profile={currentUser}
                isRound
                size={tripleSpacing}
              />
              <p className='mb-0 mt-2 text-regular semi-bold'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.YOU()}</p>
              <p className='mb-0 text-small text-gray-2'>{learnerRole}</p>
            </div>
          </div>
          {showScenarioDetails && (
            <ScenarioDetails />
          )}
          {isConversationStarted && (
            <ClickableContainer
              className='mb-0 text-primary d-flex justify-content-center scenario-toggle-button text-small semi-bold'
              onClick={handleToggleScenarioDetails}
            >
              <NvIcon
                icon='gen-ai'
                size='xss-smallest'
                className='d-flex align-items-center'
              />
              <p className='mb-0 ml-1'>
                {showScenarioDetails
                  ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.HIDE_SCENARIO()
                  : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SHOW_SCENARIO()}
              </p>
            </ClickableContainer>
          )}
          {!isConversationStarted && (
            <div className='d-flex justify-content-center mt-5'>
              <Button
                onClick={handleStartConversation}
              >
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_CONVERSATION()}
              </Button>
            </div>
          )}
          {/* AI ROLE PLAY TODO: Dummy chats will be replaced with actual conversation flow. */}
          {isConversationStarted && (
            <div className='d-flex flex-column'>
              <div className='w-100 mt-5'>
                <AiChat messageText='Hello! How can I assist you today?' />
              </div>
              <div className='w-100 mt-5'>
                <LearnerChat messageText='Hi! I have a question about the project.' />
              </div>
            </div>
          )}
        </div>
        {isConversationStarted && (
          <ChatBar />
        )}
      </div>
    </div>
  );
};

export default ConversationModalContent;
